import { ResidentialIncentiveApiData } from '../types/ResidentialIncentivesDataType';
import Item from '../types/ItemType';
import { DropdownConstants } from '../constants/DropdownConstants';
import { ApplicableVehicleType } from '../constants/ApplicableVehicleType';
import { ResidentialIncentiveStatus } from '../constants/ResidentialIncentiveStatus';
import { sortAndFormatDropdownValues } from './SortFormatDropdownUtils';

export const getUniqueGrantors = (incentives: ResidentialIncentiveApiData[]): Item[] => {
    const eligibleGrantorsSet = new Set<string>();
    eligibleGrantorsSet.add(DropdownConstants.ALL);

    incentives.forEach((incentive) => {
        incentive.grantorName.split(',').forEach((org: string) => {
            eligibleGrantorsSet.add(org.trim());
        });
    });
    return sortAndFormatDropdownValues(Array.from(eligibleGrantorsSet));
};

export const getUniqueGrantorsType = (incentives: ResidentialIncentiveApiData[]): Item[] => {
    const eligibleGrantorsTypeSet = new Set<string>();
    eligibleGrantorsTypeSet.add(DropdownConstants.ALL);

    incentives.forEach((incentive) => {
        incentive.grantorType.split(',').forEach((org: string) => {
            eligibleGrantorsTypeSet.add(org.trim());
        });
    });
    return sortAndFormatDropdownValues(Array.from(eligibleGrantorsTypeSet));
};

export const getUniqueApplicableToVehiclesType = (
    incentives: ResidentialIncentiveApiData[],
): Item[] => {
    const applicableToVehiclesSet = new Set<string>();
    applicableToVehiclesSet.add(DropdownConstants.ALL);
    if (incentives) {
        incentives.forEach((incentive) => {
            if (incentive.applicableToNewVehicles) {
                applicableToVehiclesSet.add(ApplicableVehicleType.New);
            }
            if (incentive.applicableToUsedVehicles) {
                applicableToVehiclesSet.add(ApplicableVehicleType.Used);
            }
        });
    }
    return sortAndFormatDropdownValues(Array.from(applicableToVehiclesSet));
};

export const filterResidentialIncentives = (
    residentialIncentives: ResidentialIncentiveApiData[],
    selectedResIncentiveElectricityProvider: string,
    selectedResIncentiveApplicableVehicleType: string,
    selectedResIncentiveGrantorType: string,
    selectedResidentialEligibleEquipment: string,
): ResidentialIncentiveApiData[] => {
    let filteredResidentialIncentiveData = residentialIncentives.filter((row) => {
        const matchesElectricityProvider =
            selectedResIncentiveElectricityProvider === DropdownConstants.ALL ||
            row.grantorName.includes(selectedResIncentiveElectricityProvider);

        const matchesGrantorType =
            selectedResIncentiveGrantorType === DropdownConstants.ALL ||
            row.grantorType.includes(selectedResIncentiveGrantorType);

        const matchesEquipment =
            selectedResidentialEligibleEquipment === DropdownConstants.ALL ||
            (row.eligibleEquipment &&
                row.eligibleEquipment.includes(selectedResidentialEligibleEquipment));
        return matchesElectricityProvider && matchesGrantorType && matchesEquipment;
    });

    if (selectedResIncentiveApplicableVehicleType === ApplicableVehicleType.New) {
        filteredResidentialIncentiveData = filteredResidentialIncentiveData.filter(
            (incentive) => incentive.applicableToNewVehicles === true,
        );
    } else if (selectedResIncentiveApplicableVehicleType === ApplicableVehicleType.Used) {
        filteredResidentialIncentiveData = filteredResidentialIncentiveData.filter(
            (incentive) => incentive.applicableToUsedVehicles === true,
        );
    }

    return filteredResidentialIncentiveData;
};

export const getResidentialIncentiveStatus = (isAvailable: boolean | null): string => {
    return isAvailable === true
        ? ResidentialIncentiveStatus.Current
        : ResidentialIncentiveStatus.Closed;
};

export const getUniqueEligibleEquipments = (incentives: ResidentialIncentiveApiData[]): Item[] => {
    const equipmentSet = new Set<string>();
    equipmentSet.add(DropdownConstants.ALL);

    incentives.forEach((incentive) => {
        if (Array.isArray(incentive.eligibleEquipment)) {
            incentive.eligibleEquipment.forEach((equipment) => {
                equipmentSet.add(equipment.trim());
            });
        }
    });
    return sortAndFormatDropdownValues(Array.from(equipmentSet));
};

export const getApplicableVehicleType = (incentive: ResidentialIncentiveApiData): string => {
    const vehicleTypes: string[] = [];

    if (incentive.applicableToNewVehicles) {
        vehicleTypes.push(ApplicableVehicleType.New);
    }
    if (incentive.applicableToUsedVehicles) {
        vehicleTypes.push(ApplicableVehicleType.Used);
    }

    return vehicleTypes.length > 0 ? vehicleTypes.join(' and ') : 'N/A';
};

export const getResidentialIncentiveApplicableType = (pointOfSale: boolean): string =>
    pointOfSale ? 'Deducted from purchase price' : 'Received after purchase';
